import React from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { useContactForm } from "../../hooks/useContactForm";
import { fadeVariants, VIEWPORT_ONCE } from "../../lib/motion";

const inputClass =
    "mt-2 w-full bg-[#050814] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 focus:border-[#A6E3DC] focus:outline-none transition";

const labelClass =
    "text-xs uppercase tracking-wider text-slate-400 font-semibold";

const formEnter = fadeVariants({ y: 20, duration: 0.6 });

const subjects = [
    "General Enquiry",
    "Account opening",
    "Funding & withdrawals",
    "Platform / MT5",
    "Partnerships",
];

function Field({ label, testId, ...props }) {
    return (
        <div>
            <label className={labelClass}>{label}</label>
            <input data-testid={testId} className={inputClass} {...props} />
        </div>
    );
}

function SubjectSelect({ value, onChange }) {
    return (
        <div>
            <label className={labelClass}>Subject</label>
            <select
                data-testid="contact-input-subject"
                value={value}
                onChange={onChange}
                className="mt-2 w-full bg-[#050814] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#A6E3DC] focus:outline-none transition"
            >
                {subjects.map((s) => (
                    <option key={s}>{s}</option>
                ))}
            </select>
        </div>
    );
}

function ContactFields({ form, change }) {
    return (
        <>
            <div className="grid sm:grid-cols-2 gap-5">
                <Field
                    label="Full name"
                    testId="contact-input-name"
                    required
                    value={form.name}
                    onChange={change("name")}
                    placeholder="Jane Doe"
                />
                <Field
                    label="Email"
                    testId="contact-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={change("email")}
                    placeholder="jane@example.com"
                />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
                <Field
                    label="Phone (optional)"
                    testId="contact-input-phone"
                    value={form.phone}
                    onChange={change("phone")}
                    placeholder="+1 555 010 1234"
                />
                <SubjectSelect
                    value={form.subject}
                    onChange={change("subject")}
                />
            </div>
            <div>
                <label className={labelClass}>Your message</label>
                <textarea
                    data-testid="contact-input-message"
                    required
                    value={form.message}
                    onChange={change("message")}
                    rows={6}
                    placeholder="How can we help?"
                    className={`${inputClass} resize-none`}
                />
            </div>
        </>
    );
}

export default function ContactForm() {
    const { form, loading, change, submit } = useContactForm();

    return (
        <motion.form
            variants={formEnter}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-3 xpo-glass p-8 lg:p-10 space-y-5"
        >
            <ContactFields form={form} change={change} />
            <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center"
            >
                <Send size={16} /> {loading ? "Sending…" : "Send message"}
            </button>
            <p className="text-xs text-slate-500 text-center">
                By submitting, you agree to our Privacy Policy. We never share
                your data.
            </p>
        </motion.form>
    );
}
